import { Link } from "react-router-dom";
import { SeoTags } from "../../components/Head/SeoTags";
import { useTranslation } from "react-i18next";
import { LogoOnly } from "../../assets/icons/logoOnly";
import TrademarkDisclaimer from "../../components/Legal/TrademarkDisclaimer";

const CompetitorAlternative = ({ competitor }) => {
  const { t } = useTranslation();
  const base = `alternatives.${competitor}`;

  // Competitor-specific data. Instapaper and Omnivore have their own bespoke
  // pages (InstapaperAlternative / OmnivoreAlternative); this generic template
  // now backs the living-competitor comparisons only.
  //
  // We intentionally do NOT reference competitor logos here. Naming a
  // competitor for comparison is nominative fair use, but reproducing their
  // logo is the weakest legal footing — and hotlinking their favicon also
  // leaked visitor requests to their servers. Use a neutral icon instead.
  const competitorData = {
    raindrop: {
      name: "Raindrop.io",
      icon: "bi-bookmark",
      color: "primary",
      features: {
        hasEncryption: false,
        hasAI: false,
        hasMultiCollection: true,
        hasFreeUnlimited: false,
        hasSpaces: false
      }
    }
  };

  const data = competitorData[competitor];

  const faqData = [
    { questionKey: `${base}.faq.migration.question`, answerKey: `${base}.faq.migration.answer` },
    { questionKey: `${base}.faq.free.question`, answerKey: `${base}.faq.free.answer` },
    { questionKey: `${base}.faq.features.question`, answerKey: `${base}.faq.features.answer` },
    { questionKey: `${base}.faq.privacy.question`, answerKey: `${base}.faq.privacy.answer` }
  ];

  return (
    <>
      <SeoTags
        title={t(`${base}.seo.title`)}
        description={t(`${base}.seo.description`)}
        keywords={t(`${base}.seo.keywords`)}
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "onBookmarks",
            applicationCategory: "ProductivityApplication",
            operatingSystem: "Web, iOS, Android",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            alternativeHeadline: `Best ${data.name} Alternative`,
            description: t(`${base}.seo.description`),
          }),
        }}
      />

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: t(faq.questionKey),
              acceptedAnswer: {
                "@type": "Answer",
                text: t(faq.answerKey),
              },
            })),
          }),
        }}
      />

      {/* Hero Section */}
      <section className="hero bg-light">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-8 mx-auto text-center">
              <div>
                <div className="d-inline-block rounded-4 bg-primary-subtle fs-normal text-primary px-3 py-2 mb-4">
                  <i className="bi bi-arrow-repeat me-2"></i>
                  {t(`${base}.hero.badge`)}
                </div>
              </div>
              <h1 className="display-4 fw-bold mb-4">
                {t(`${base}.hero.title`)}
              </h1>
              <p className="lead mb-4">
                {t(`${base}.hero.description`)}
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-primary btn-lg px-5"
                >
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  {t(`${base}.hero.cta`)}
                </Link>
                <Link to="/features" className="btn btn-outline-primary btn-lg px-4">
                  {t(`${base}.hero.ctaSecondary`)}
                </Link>
              </div>
              <p className="mt-3 text-muted">
                <i className="bi bi-shield-check me-2"></i>
                {t(`${base}.hero.guarantee`)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-4 bg-white border-bottom">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center gap-5 flex-wrap">
                <div className="text-center">
                  <i className="bi bi-shield-lock h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">Zero-knowledge encryption</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-robot h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">AI-powered organization</small>
                </div>
                <div className="text-center">
                  <i className="bi bi-collection h2 mb-0 text-primary d-block"></i>
                  <small className="text-muted">Multi-collection &amp; Spaces</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Switch Section */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">
              {t(`${base}.whySwitch.title`)}
            </h2>
            <p className="lead text-muted">
              {t(`${base}.whySwitch.description`)}
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-primary-subtle rounded-3 mb-3">
                    <i className="bi bi-robot fs-3 text-primary"></i>
                  </div>
                  <h3 className="h5 mb-3">{t(`${base}.whySwitch.ai.title`)}</h3>
                  <p className="text-muted mb-3">{t(`${base}.whySwitch.ai.description`)}</p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t(`${base}.whySwitch.ai.benefit1`)}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t(`${base}.whySwitch.ai.benefit2`)}
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      {t(`${base}.whySwitch.ai.benefit3`)}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-shield-lock fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">{t(`${base}.whySwitch.privacy.title`)}</h3>
                  <p className="text-muted mb-3">{t(`${base}.whySwitch.privacy.description`)}</p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t(`${base}.whySwitch.privacy.benefit1`)}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t(`${base}.whySwitch.privacy.benefit2`)}
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      {t(`${base}.whySwitch.privacy.benefit3`)}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-success-subtle rounded-3 mb-3">
                    <i className="bi bi-collection fs-3 text-success"></i>
                  </div>
                  <h3 className="h5 mb-3">{t(`${base}.whySwitch.organization.title`)}</h3>
                  <p className="text-muted mb-3">{t(`${base}.whySwitch.organization.description`)}</p>
                  <ul className="list-unstyled small text-muted">
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t(`${base}.whySwitch.organization.benefit1`)}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check text-success me-2"></i>
                      {t(`${base}.whySwitch.organization.benefit2`)}
                    </li>
                    <li>
                      <i className="bi bi-check text-success me-2"></i>
                      {t(`${base}.whySwitch.organization.benefit3`)}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-4">
                  {t(`${base}.comparison.title`)}
                </h2>
              </div>

              <div className="table-responsive rounded-3 border shadow-sm bg-white">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="p-3">Feature</th>
                      <th scope="col" className="p-3 text-center bg-light">
                        <i className={`bi ${data.icon} me-2 text-muted`}></i>
                        {data.name}
                      </th>
                      <th scope="col" className="p-3 text-center d-flex align-items-center justify-content-center gap-2">
                        <LogoOnly width="20" />
                        onBookmarks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 fw-medium">AI-Powered Organization</td>
                      <td className="p-3 text-center bg-light">
                        {data.features.hasAI ? (
                          <i className="bi bi-check-circle text-success"></i>
                        ) : (
                          <i className="bi bi-x-circle text-danger"></i>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Full AI Tagging</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Client-Side Encryption</td>
                      <td className="p-3 text-center bg-light">
                        {data.features.hasEncryption ? (
                          <i className="bi bi-check-circle text-success"></i>
                        ) : (
                          <i className="bi bi-x-circle text-danger"></i>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Zero-Knowledge</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Multi-Collection Assignment</td>
                      <td className="p-3 text-center bg-light">
                        {data.features.hasMultiCollection ? (
                          <i className="bi bi-check-circle text-success"></i>
                        ) : (
                          <i className="bi bi-x-circle text-danger"></i>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Workspace Spaces</td>
                      <td className="p-3 text-center bg-light">
                        {data.features.hasSpaces ? (
                          <i className="bi bi-check-circle text-success"></i>
                        ) : (
                          <i className="bi bi-x-circle text-danger"></i>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Multiple Spaces</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Free Unlimited Bookmarks</td>
                      <td className="p-3 text-center bg-light">
                        {data.features.hasFreeUnlimited ? (
                          <i className="bi bi-check-circle text-success"></i>
                        ) : (
                          <span className="text-muted">Limited</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <strong className="text-success">Unlimited Free</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Browser Extensions</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                      <td className="p-3 text-center">
                        <strong>All Major Browsers</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 fw-medium">Cross-Device Sync</td>
                      <td className="p-3 text-center bg-light">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                      <td className="p-3 text-center">
                        <i className="bi bi-check-circle text-success"></i>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h3 className="h2 fw-bold mb-4">{t(`${base}.faq.title`)}</h3>
          </div>

          <div className="row g-4">
            {faqData.map((faq, index) => (
              <div key={index} className="col-lg-6">
                <div className="h-100">
                  <h5 className="fw-bold mb-3">{t(faq.questionKey)}</h5>
                  <p className="text-muted">{t(faq.answerKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-5 banner-gradient text-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h4 className="h1 fw-bold mb-4 text-white">
                {t(`${base}.cta.title`)}
              </h4>
              <p className="lead mb-4">
                {t(`${base}.cta.description`)}
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-light btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  {t(`${base}.cta.button`)}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrademarkDisclaimer names={data.name} />
    </>
  );
};

export default CompetitorAlternative;
